<template>
    <div class="review-header">
        <div v-if="!readonly && timer" class="review-header__cancel">
            <div v-if="timer" class="review-header__timer">
                До завершения проверки осталось: <strong>{{ timer }}</strong>
            </div>
            <RsvButton
                color="red"
                text="Отказаться от проверки"
                @click="cancelReviewSession"
            />
        </div>
        <div class="review-header__name">{{ review.name }}</div>
        <div class="review-header__session-id">ID работы {{ review.sessionId }}</div>
        <Collapsible title="Подсказка по проверке работ">
            <div class="review-header__hint" v-html="review.hint" />
        </Collapsible>

        <Dialog class="expiration-dialog" :is-popup="showExpiration" @close="gotoEvents">
            <div class="expiration-dialog__title">
                Время на проверку текущей работы закончилось
            </div>
            <div class="expiration-dialog__footer">
                <RsvButton color="blue" text="ОК" @click="gotoEvents" />
            </div>
        </Dialog>
    </div>
</template>

<script>
import Collapsible from "@/components/UI/Collapsible.vue";
import { post } from "@/tools/http";
import moment from "moment";
import declinationOfNumber from "@/tools/declinationOfNumber";
import Dialog from "@/components/UI/Dialog.vue";
import RsvButton from "@/components/UI/RsvButton/RsvButton.vue";
import { REVIEW_SESSION_STATUS_CANCELED, REVIEW_SESSION_STATUS_NEW } from "@/consts/examination";
import getApi from "@/tools/api/examination";

export default {
    name: "ExpertPanelReviewHeader",
    components: {
        Collapsible,
        Dialog,
        RsvButton,
    },
    props: {
        review: Object,
        session: Object,
        readonly: Boolean,
    },
    data() {
        return {
            intervalObj: null,
            secondsRemain: 0,
            showExpiration: false,
            examinationApi: getApi(),
        };
    },
    computed: {
        timer() {
            if (this.secondsRemain <= 0) {
                return '';
            }
            let minutes = Math.floor(this.secondsRemain / 60) % 60;
            if (minutes > 0) {
                minutes = `${minutes} ${declinationOfNumber(minutes, 'минута', 'минуты', 'минут')}`;
            }
            let hours = Math.floor(this.secondsRemain / 3600) % 24;
            if (hours > 0) {
                hours = `${hours} ${declinationOfNumber(hours, 'час', 'часа', 'часов')}`;
            }
            let days = Math.floor(this.secondsRemain / 3600 / 24);
            if (days > 0) {
                days = `${days} ${declinationOfNumber(days, 'день', 'дня', 'дней')}`;
            }
            let ret = [days, hours, minutes].filter(Boolean).join(' ');
            if (ret === '') {
                if (this.secondsRemain < 30) {
                    ret = 'меньше 30 сек';
                } else {
                    ret = 'меньше минуты';
                }
            }
            return ret;
        },
    },
    methods: {
        async cancelReviewSession() {
            const res = await this.examinationApi.cancelSession(this.session.id);
            if (res) {
                this.$router.push({name: 'expert-panel-event', type: 'review', event: this.review.id});
            }
        },
        gotoEvents() {
            this.$router.push({
                name: 'expert-panel-event',
                params: {
                    type: 'review',
                    event: this.review.id,
                },
            });
        },
    },
    mounted() {
        if (this.session.status === REVIEW_SESSION_STATUS_NEW) {
            let dateEnd;
            if (this.review.reviewHours) {
                dateEnd = moment(this.session.createdAt).add(this.review.reviewHours, 'hours');
            } else {
                dateEnd = moment(this.review.endAt);
            }
            this.secondsRemain = dateEnd.diff(moment(), 'seconds');
            this.intervalObj = setInterval(async () => {
                this.secondsRemain = dateEnd.diff(moment(), 'seconds');
                if (this.secondsRemain <= 0) {
                    const res = await this.examinationApi.checkSession(this.session.id);
                    if (res !== null) {
                        if (res.status === REVIEW_SESSION_STATUS_CANCELED) {
                            clearInterval(this.intervalObj);
                            this.showExpiration = true;
                        }
                    }
                }
            }, 5000);
        }

    },
    beforeDestroy() {
        if (this.intervalObj) {
            clearInterval(this.intervalObj);
        }
    },
};
</script>

<style lang="scss" scoped>
@import '@/styles/rem';

.review-header {
  font-family: Montserrat, sans-serif;
  background: #FFFFFF;
  box-shadow: 0 0 rem(16) rgba(70, 99, 171, 0.2);
  border-radius: rem(16);
  padding: rem(30) rem(40);

  &__timer {
    font-weight: 400;
    font-size: rem(14);
    line-height: rem(24);
    color: #2A2626;
    margin-bottom: rem(16);
  }
  &__name {
    font-weight: 600;
    font-size: rem(24);
    line-height: rem(26);
    color: #131614;
  }
  &__session-id {
    font-weight: 500;
    font-size: rem(14);
    line-height: rem(26);
    color: #98A0A9;
    margin-bottom: rem(8);
  }
  &__cancel {
    float: right;
    text-align: right;
    :deep(.rsv-button) {
      display: inline-flex !important;
    }
  }
  &__hint {
    :deep(*) {
      margin: 0;
      font-family: Montserrat, sans-serif;
      font-weight: 400;
      font-size: rem(16);
      line-height: rem(20);
    }
    :deep(h2) {
      font-size: rem(60);
      line-height: rem(60);
      margin-top: rem(50);
      margin-bottom: rem(50);
      font-weight: 300;
      * {
        font-size: rem(60);
        line-height: rem(60);
      }
    }
    :deep(h3) {
      font-size: rem(48);
      line-height: rem(50);
      margin-top: rem(48);
      margin-bottom: rem(48);
      font-weight: 400;
      * {
        font-size: rem(48);
        line-height: rem(50);
      }
    }
    :deep(strong) {
      font-weight: 700;
    }
    :deep(a) {
      color: #4663AB;
      text-decoration: underline;
    }
    :deep(ul), :deep(ol) {
      margin: rem(14) 0;
    }
    :deep(p) {
      margin-bottom: rem(8);
    }
    :deep(blockquote) {
      border-left: 2px solid #ccc;
      margin-left: 1.5rem;
      padding-left: 1rem;
    }
  }
  .rsv-button_color_red {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    padding: rem(14) rem(32);
    gap: rem(10);
    background: #DB6C61;
    border-radius: rem(10);

    font-family: Montserrat, sans-serif;
    font-weight: 600;
    font-size: rem(14);
    line-height: rem(20);
    color: #FFFFFF;
  }
}
.expiration-dialog {
  :deep(.dialog) {
    max-width: rem(680);
  }
  &__title {
    font-family: Montserrat, sans-serif;
    font-weight: 600;
    font-size: rem(20);
    line-height: rem(30);
    text-align: center;
    color: #000000;
    margin-top: rem(16);
    margin-bottom: rem(16);
  }
  &__footer {
    display: flex;
    justify-content: center;
    gap: rem(22);
    :deep(.rsv-button_color_white) {
      padding: rem(14) rem(32);
      background: #fff;
      border: solid 1px #4663AB;
      color: #4663ab;
      border-radius: rem(10);
    }
  }
}
</style>